import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components/macro'

import 'pannellum/build/pannellum.css'
import 'pannellum'

import Typography from 'components/molecules/typography'

const Container = styled.div`
  height: ${({ $ratio }) => ($ratio ? 0 : 100)}%;
  overflow: hidden;
  padding-top: ${({ $ratio }) => ($ratio ? 100 / $ratio : 0)}%;
  position: relative;
  width: 100%;
`

/* Sized before the viewer initialises too: it only adds its own class once it
   starts, and the observer above needs a real box to measure. */
const Viewer = styled.div`
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;

  /* The viewer puts its class on this same element, so no descendant selector.
     Repeated here to outweigh its own .pnlm-container positioning. */
  &.pnlm-container {
    background: ${({ theme }) => theme.colors.darkestBackground};
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }

  /* It renders an English-only error with an inline display, which only
     !important can override. Ours is translated and shown instead. */
  .pnlm-error-msg {
    display: none !important;
  }
`

const ErrorMessage = styled(Typography)`
  background-color: ${({ theme }) => theme.colors.darkestBackground};
  border-radius: ${({ theme }) => theme.sizings.lvl1};
  color: ${({ theme }) => theme.colors.textOnDarkBackground};
  left: 50%;
  margin: 0;
  max-width: 80%;
  padding: ${({ theme }) => theme.sizings.lvl2}
    ${({ theme }) => theme.sizings.lvl3};
  position: absolute;
  text-align: center;
  top: 50%;
  transform: translate(-50%, -50%);
`

/**
 * Renders an equirectangular (360°) photo in a sphere viewer the user can drag
 * to look around in, zoom and open full screen.
 *
 * Each viewer holds a WebGL context and browsers only allow a handful of those,
 * so the sphere is only mounted while it is actually on screen. In a carousel
 * every slide (including the clones react-slick adds) is in the DOM at once.
 */
const SphereImageViewer = ({ className, src, ratio }) => {
  const containerRef = useRef(null)
  const isMounted = useRef(true)
  const [isOnScreen, setIsOnScreen] = useState(!window.IntersectionObserver)
  const [inError, setInError] = useState(false)
  const { t } = useTranslation()

  useEffect(
    () => () => {
      isMounted.current = false
    },
    [],
  )

  useEffect(() => {
    const container = containerRef.current

    if (!container || !window.IntersectionObserver) {
      return undefined
    }

    const observer = new window.IntersectionObserver(
      ([entry]) => setIsOnScreen(entry.isIntersecting),
      { threshold: 0.1 },
    )
    observer.observe(container)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const container = containerRef.current

    if (!container || !src || !isOnScreen) {
      return undefined
    }

    if (!window.pannellum) {
      setInError(true)
      return undefined
    }

    setInError(false)

    const viewer = window.pannellum.viewer(container, {
      type: 'equirectangular',
      panorama: src,
      autoLoad: true,
      // A still panorama is fetched over XHR and read as a blob, so the image
      // host has to allow cross origin requests. Without credentials.
      crossOrigin: 'anonymous',
      showZoomCtrl: true,
      showFullscreenCtrl: true,
      autoRotate: -2,
      autoRotateInactivityDelay: 3000,
    })

    // The request can still fail after the viewer is destroyed.
    viewer.on('error', () => isMounted.current && setInError(true))

    return () => viewer.destroy()
  }, [src, isOnScreen])

  useEffect(() => {
    const container = containerRef.current

    if (!container) {
      return undefined
    }

    // The overlay listens for arrow keys on the document to move between
    // photos. Inside the sphere they belong to the viewer, or looking around
    // would jump to the next photo halfway through. Escape still closes.
    const keepArrowKeys = (event) => {
      if (event.key.indexOf('Arrow') === 0) {
        event.stopPropagation()
      }
    }

    container.addEventListener('keydown', keepArrowKeys)

    return () => container.removeEventListener('keydown', keepArrowKeys)
  }, [])

  // Keep dragging inside the sphere from also swiping the surrounding carousel.
  const stopDrag = (event) => event.stopPropagation()

  return (
    <Container
      className={className}
      $ratio={ratio}
      onMouseDown={stopDrag}
      onTouchStart={stopDrag}
      onTouchMove={stopDrag}
    >
      <Viewer ref={containerRef} />
      {inError && (
        <ErrorMessage type="ExplanationParagraph">
          {t('carFileMediaTab.sphere360.viewerError')}
        </ErrorMessage>
      )}
    </Container>
  )
}

SphereImageViewer.propTypes = {
  className: PropTypes.string,
  /** Source of the equirectangular photo */
  src: PropTypes.string.isRequired,
  /** Width divided by height, leave out to fill the available height */
  ratio: PropTypes.number,
}

SphereImageViewer.defaultProps = {
  className: null,
  ratio: undefined,
}

export default SphereImageViewer
